import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { KxRoiStrip } from './KxRoi';
import { Users, Crown, RefreshCw, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import { useRest, RCard, RHead, RStat, RestLoading, money, ModuleNotConnected } from './restui';

const TIER = {
  vip: { es: 'VIP', en: 'VIP', cls: 'bg-amber-50 text-amber-600 border-amber-200' },
  regular: { es: 'Habitual', en: 'Regular', cls: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
  new: { es: 'Nuevo', en: 'New', cls: 'bg-sky-50 text-sky-600 border-sky-200' },
};

const lastVisit = (iso, isEs) => {
  const days = Math.max(0, Math.floor((Date.now() - new Date(`${iso}T12:00:00`).getTime()) / 86400000));
  if (days === 0) return isEs ? 'hoy' : 'today';
  if (days === 1) return isEs ? 'ayer' : 'yesterday';
  return isEs ? `hace ${days} d` : `${days}d ago`;
};

export default function RestGuests() {
  const { isEs } = useOutletContext();
  const [page, setPage] = useState(1);
  const { data: d, loading } = useRest('/guests');
  if (loading && !d) return <RestLoading />;
  if (!d?.has_data) {
    return <ModuleNotConnected isEs={isEs} title="Guests" testid="rest-module-guests"
      desc_en="Repeat customers, lifetime value, visit frequency and segments." desc_es="Clientes recurrentes, lifetime value, frecuencia de visita y segmentos."
      needs={['POS customer data', 'Loyalty program', 'Online ordering accounts']} />;
  }
  const s = d.summary;
  return (
    <div data-testid="rest-guests">
      <RHead title="Guests" sub={isEs ? 'Quién vuelve, cuánto vale cada cliente y a quién mimar.' : 'Who comes back, what each guest is worth, and who to pamper.'} />
      <KxRoiStrip isEs={isEs} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <RStat label={isEs ? 'Clientes con perfil' : 'Guests tracked'} value={s.tracked.toLocaleString()} testid="rest-guests-tracked" />
        <RStat label={isEs ? 'Tasa de retorno' : 'Returning rate'} value={`${s.returning_rate}%`} sub={isEs ? 'vuelven en 30 días' : 'return within 30 days'} testid="rest-guests-returning" />
        <RStat label={isEs ? 'Valor de vida medio' : 'Avg lifetime value'} value={money(s.avg_ltv)} testid="rest-guests-ltv" />
        <RStat label="VIPs" value={s.vips} sub={isEs ? `${s.new_this_month} nuevos este mes` : `${s.new_this_month} new this month`} testid="rest-guests-vips" />
      </div>

      <div className="mt-5 mb-2 flex items-center justify-between flex-wrap gap-2">
        <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold text-gray-400">
          <Crown className="w-3.5 h-3.5 text-amber-500" /> {isEs ? 'Tus mejores clientes' : 'Your top guests'}
        </p>
        <span className="inline-flex items-center gap-1 text-[10.5px] text-gray-400">
          <Sparkles className="w-3 h-3 text-[var(--rest-accent)]" /> {isEs ? 'Modelado por AI desde tus ventas importadas' : 'AI-modeled from your imported sales'}
        </span>
      </div>
      <RCard className="p-2" testid="rest-guests-list">
        {(() => {
          const PER = 10;
          const pages = Math.max(1, Math.ceil(d.guests.length / PER));
          const cur = Math.min(page, pages);
          return d.guests.slice((cur - 1) * PER, cur * PER).map((g) => {
          const t = TIER[g.tier];
          return (
            <div key={g.name} className="flex items-center gap-3 px-3 py-2.5 border-b border-gray-50 last:border-0 hover:bg-[var(--rest-accent-soft)] rounded-xl transition-colors" data-testid={`rest-guest-${g.name.replace(/\W/g, '')}`}>
              <span className="w-9 h-9 shrink-0 rounded-full bg-[var(--rest-accent-soft)] border border-gray-200 text-[var(--rest-accent)] text-[13px] font-bold flex items-center justify-center">
                {g.name.slice(0, 1)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[13.5px] font-semibold text-gray-900 truncate">
                  {g.name}
                  <span className={`ml-2 rounded-full border px-1.5 py-0.5 text-[9.5px] font-bold uppercase align-middle ${t.cls}`}>{isEs ? t.es : t.en}</span>
                </p>
                <p className="text-[11px] text-gray-400 truncate">
                  <RefreshCw className="w-3 h-3 inline mr-1 -mt-0.5" />{g.visits} {isEs ? 'visitas' : 'visits'} · {isEs ? 'favorito' : 'favorite'}: <b className="text-gray-600">{g.favorite}</b> · {g.channel}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-[14px] font-black tabular-nums text-gray-900">{money(g.ltv)}</p>
                <p className="text-[10.5px] text-gray-400">{isEs ? 'última visita' : 'last visit'} {lastVisit(g.last_visit, isEs)}</p>
              </div>
            </div>
          );
          });
        })()}
      </RCard>
      {d.guests.length > 10 && (() => {
        const pages = Math.ceil(d.guests.length / 10);
        const cur = Math.min(page, pages);
        return (
          <div className="mt-3 flex items-center justify-center gap-2" data-testid="rest-guests-pager">
            <button onClick={() => setPage(cur - 1)} disabled={cur <= 1} data-testid="rest-guests-prev"
              className="p-1.5 rounded-full border border-gray-200 text-gray-500 disabled:opacity-30 hover:border-[var(--rest-accent)] hover:text-[var(--rest-accent)] transition-colors"><ChevronLeft className="w-4 h-4" /></button>
            {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
              <button key={n} onClick={() => setPage(n)} data-testid={`rest-guests-page-${n}`}
                className={`w-8 h-8 rounded-full text-[12px] font-bold tabular-nums transition-colors ${n === cur ? 'bg-[var(--rest-accent)] text-white' : 'border border-gray-200 text-gray-500 hover:border-[var(--rest-accent)] hover:text-[var(--rest-accent)]'}`}>{n}</button>
            ))}
            <button onClick={() => setPage(cur + 1)} disabled={cur >= pages} data-testid="rest-guests-next"
              className="p-1.5 rounded-full border border-gray-200 text-gray-500 disabled:opacity-30 hover:border-[var(--rest-accent)] hover:text-[var(--rest-accent)] transition-colors"><ChevronRight className="w-4 h-4" /></button>
          </div>
        );
      })()}
      <p className="mt-3 text-[11px] text-gray-400 inline-flex items-center gap-1.5">
        <Users className="w-3.5 h-3.5" /> {isEs
          ? 'Conecta tu POS o programa de fidelidad para pasar de estimaciones AI a clientes reales con nombre y email.'
          : 'Connect your POS or loyalty program to turn AI estimates into real guests with names and emails.'}
      </p>
    </div>
  );
}
